import { useEffect, useRef, useState } from 'react';
import { Button, Notification } from 'grommet';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Twitch } from '../../icons/Twitch';
import {
  getCurrentRoute,
  getTwitchAuthorizationUrl,
  saveAuthReturnTo
} from '../../auth/authNavigation';
import { useLazyGetAuthMeQuery } from '../../api/authApi';
import { authInProgressSelector, setIsAuthenticating } from '../../slices/authSlice';

export const TwitchAuth = () => {
  const location = useLocation();
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const authInProgress = useSelector(authInProgressSelector);
  const redirectStartedRef = useRef(false);
  const [redirectStarted, setRedirectStarted] = useState(false);
  const [showAuthError, setShowAuthError] = useState(false);
  const [checkBackend] = useLazyGetAuthMeQuery();

  useEffect(() => {
    if (!showAuthError) {
      return undefined;
    }

    const timeoutId = setTimeout(() => setShowAuthError(false), 8000);
    return () => clearTimeout(timeoutId);
  }, [showAuthError]);

  const handleTwitchSignIn = async () => {
    if (redirectStartedRef.current || authInProgress) {
      return;
    }

    redirectStartedRef.current = true;
    setRedirectStarted(true);

    try {
      const result = await checkBackend();
      const status = result.error?.status;

      if (result.error && status !== 401 && status !== 403) {
        redirectStartedRef.current = false;
        setRedirectStarted(false);
        setShowAuthError(true);
        return;
      }
    } catch {
      redirectStartedRef.current = false;
      setRedirectStarted(false);
      setShowAuthError(true);
      return;
    }

    saveAuthReturnTo(getCurrentRoute(location));
    dispatch(setIsAuthenticating(true));
    window.location.assign(getTwitchAuthorizationUrl());
  };

  return (
    <>
      <Button
        primary
        busy={redirectStarted}
        onClick={handleTwitchSignIn}
        disabled={redirectStarted || authInProgress}
        label={t('signIn')}
        icon={<Twitch />}
      />
      {showAuthError && (
        <Notification
          toast={{ position: 'top-right' }}
          status="critical"
          title={t('authErrorTitle')}
          message={t('authError')}
          onClose={() => setShowAuthError(false)}
        />
      )}
    </>
  );
};
